import type { BookableSettings } from "@@/shared/acquire/bookable-settings";
import type { ResourceGraph } from "@@/shared/acquire/resources/resource-graph";
import { isResourceGroup } from "@@/shared/acquire/resources/resource-group";
import type { ResourceReservationRequestInfo } from "@@/shared/acquire/resources/resource-reservation-info";
import { isResourceTableGroup } from "@@/shared/acquire/resources/resource-table-group";
import type { ResourceAvailabilityTimeRange } from "@@/shared/acquire/sessions/bookable-session-helper";
import { isResourceAvailable } from "@@/shared/acquire/sessions/bookable-session-helper";
import type { BookableSessionId } from "@@/shared/acquire/sessions/bookable-session-id";
import { calculateQuantityPerResource } from "@@/shared/acquire/sessions/create-reservation-service";
import { flattenGraph } from "@@/shared/acquire/sessions/flatten-graph";
import { asDate } from "@@/shared/date-time/as-date";
import { convertTimeByGranularity } from "@@/shared/date-time/time-granularity";
import type { TimeRange } from "@@/shared/date-time/time-range";
import {
    addMinutesTimeToTimeRange,
    timeRangeByDurationFactory,
} from "@@/shared/date-time/time-range";
import type { DurationInMinutes } from "@@/shared/date-time/time-spans";
import type { Sku } from "@@/shared/products/sku";
import { sortBy } from "@@/utilities/sort";
import { sum } from "@@/utilities/sum";
import { isFuture } from "date-fns";
import type { Bookable } from "../bookable";
import { getBookableResourceReferences } from "../bookable";
import { getOverrideResourceIds } from "../resource-references/resource-override";
import type { Resource } from "../resources/resource";
import type { ResourceId } from "../resources/resource-id";
import type { ResourceLocal } from "../resources/resource-local";
import type { ResourceShared } from "../resources/resource-shared";
import type { BookableSession } from "./bookable-session";

/**
 * Contains availability information about a resource.
 * @param resourceId Id of the resource
 * @param available If the resource is available
 * @param availableResources List of sub resources available if the `resourceId` is referencing resource group, else it is empty
 */
type ResourceAvailability = {
    /**
     * The resource the availability information is referenced to
     * @type {ResourceId}
     */
    resourceId: ResourceId;
    /**
     * Answers the question if the resource or any children (if `resourceId` references a resource group) are available
     * @type {boolean}
     */
    available: boolean;
    /**
     *
     * List of available sub resources if the `resourceId` is referencing resource group, else it is empty
     * @type {Resource[]}
     */
    availableSubResources: Resource[];
    subResourceCount_available: number;
    subResourceCount_unavailable: number;
};
/**
 * Contains availability information about available for a specific time range.
 */
type TimeRangeResourceAvailability = {
    timeRange: TimeRange;
    resourcesAvailability: ResourceAvailability[];
};

const findAllActiveResources = (
    sku: Sku,
    sessions: BookableSession[],
    allSessions = false,
) => {
    if (sku.acquire._type !== "BOOKABLE") return [];
    return findAllActiveResourcesByBookable(sku.acquire, sessions, allSessions);
};

type SharedOrLocalResource = ResourceLocal | ResourceShared;

const findAllLocalMainResources = (
    sku: Sku,
    _sessions: BookableSession[],
): SharedOrLocalResource[] => {
    if (sku.acquire._type !== "BOOKABLE") return [];
    const bookable = sku.acquire;
    const localResourceReferences =
        bookable.main._type === "RESOURCE_REFERENCE"
            ? [bookable.main]
            : bookable.main.references;

    const list = localResourceReferences.flatMap(r => {
        if (
            r.resource._type === "RESOURCE_LOCAL" ||
            r.resource._type === "RESOURCE_SHARED"
        ) {
            return r.resource;
        }
        return [];
    });

    return list;
};

const findAllActiveResourcesByBookable = (
    bookable: Bookable,
    sessions: BookableSession[],
    allSessions = false, // If true, all sessions are considered, otherwise only future sessions
) => {
    const skuReferences = getBookableResourceReferences(bookable);
    const def = getBookableResourceReferences(bookable).flatMap(r =>
        r.resource._type === "RESOURCE_GROUP"
            ? r.resource.resources.map(r => r._id)
            : r.resource._id,
    );

    if (sessionOverridesResourceIds(bookable)) {
        const others = sessions
            .filter(session =>
                allSessions ? true : isFuture(asDate(session.start)),
            )
            .flatMap(session => {
                return skuReferences.flatMap(ref =>
                    getOverrideResourceIds(session, ref),
                );
            });
        return [...new Set([...def, ...others])];
    }

    return [...new Set([...def])];
};

type ResourceTimeRange = {
    resourceId: ResourceId;
    timeRanges: TimeRange[];
};

/**
 * Calculate availability for a list of resources and a requested quantity
 * @param resources The resources to calculate availability for
 * @param requestedQuantities The requested quantities for each resource
 * @param globalResources The global resources
 * @param resourceTimeRange The time ranges for each resource
 * @param resourceReservationInfo The resource reservation information
 * @param settings The bookable settings
 * @param options Additional options
 * @returns The availability for each resource
 */
const calculateAvailabilitySpecialized = (
    resources: Resource[],
    requestedQuantities: Map<ResourceId, number>,
    globalResources: ResourceGraph[],
    resourceTimeRange: ResourceTimeRange[],
    resourceReservationInfo: ResourceReservationRequestInfo[],
    settings: BookableSettings,
    options?: {
        requestedResources?: ResourceId[];
        allowSplitBookings: boolean; //If true, split bookings are allowed example 2 tables can be booked
        maxGuestPerTime?: number; //If set, the maximum number of guests per start time
        limitTableToClosest?: boolean; //If true, the pickable table will be limited to the closest available table
        sessionId?: BookableSessionId;
        overrideSession?: boolean;
    },
) => {
    return resources
        .flatMap(resource => {
            const resourceId = resource._id;
            const ranges = resourceTimeRange.find(
                f => f.resourceId === resourceId,
            );

            if (!ranges)
                throw new Error("Problem att hitta tillgängliga tider.");
            const requestedQuantity = requestedQuantities.get(resourceId) ?? 0;

            const globalResourcesUsed = globalResources
                .filter(r => r.resourceGroup?._id === resourceId)
                .flatMap(graph => flattenGraph(graph));

            return ranges.timeRanges.map(resourceTimeRange => {
                if (
                    options?.maxGuestPerTime !== undefined &&
                    options?.maxGuestPerTime > 0
                ) {
                    // If we limit the maxGuestPerTime
                    // we need to check if the amount is over the limit
                    const summedQuantityOnStartTime = sum(
                        resourceReservationInfo
                            .filter(
                                f =>
                                    f.actualTimeRange.start.unix ===
                                    resourceTimeRange.start.unix,
                            )
                            .filter(
                                r => r.resourceType !== "RESOURCE_TABLE_GROUP",
                            ),
                        r => r.reservedQuantity,
                    );

                    // console.log({
                    //     message: "Summed quantity on start time",
                    //     summedQuantityOnStartTime,
                    //     startTime: resourceTimeRange.start.iso,
                    //     resourceReservationInfo,
                    // });

                    // If we limit the maxGuestPerTime, we need to check if the amount is over the limit
                    if (
                        summedQuantityOnStartTime + requestedQuantity >
                        options.maxGuestPerTime
                    ) {
                        const result: ResourceAvailability = {
                            resourceId,
                            available: false,
                            availableSubResources: [],
                            subResourceCount_available: 0,
                            subResourceCount_unavailable:
                                globalResourcesUsed.length,
                        };

                        return result;
                    }
                }

                if (isResourceGroup(resource)) {
                    const [toBookResources, availableResources] =
                        calculateQuantityPerResource(
                            globalResourcesUsed,
                            resourceTimeRange,
                            settings,
                            resourceReservationInfo,
                            requestedQuantity,
                            undefined, // options?.requestedResources,
                            options?.allowSplitBookings,
                            options?.sessionId,
                            options?.overrideSession,
                        );

                    const isTable = toBookResources.some(
                        t => t.resource._type === "RESOURCE_TABLE",
                    );

                    if (isTable && !options?.allowSplitBookings) {
                        //Does it exit one table that can be booked
                        const find = toBookResources.filter(
                            t => t.quantity === requestedQuantity,
                        );
                        const filteredResources = options?.limitTableToClosest
                            ? filterBestMatchResources(
                                  requestedQuantity,
                                  availableResources,
                              )
                            : availableResources;
                        const availableSubResources = filteredResources.map(
                            ar => ar.resource,
                        );
                        const result: ResourceAvailability = {
                            resourceId,
                            available: find.length > 0,
                            availableSubResources,
                            subResourceCount_available:
                                availableSubResources.length,
                            subResourceCount_unavailable:
                                globalResourcesUsed.length -
                                availableSubResources.length,
                        };

                        return result;
                    } else {
                        //Find the maximum amount
                        const sums = sum(toBookResources, r => r.quantity);
                        const availableSubResources = availableResources.map(
                            ar => ar.resource,
                        );

                        // console.error({
                        //     sums,
                        //     requestedQuantity,
                        // });

                        const result: ResourceAvailability = {
                            resourceId,
                            available: sums >= requestedQuantity,
                            availableSubResources,
                            subResourceCount_available:
                                availableSubResources.length,
                            subResourceCount_unavailable:
                                globalResourcesUsed.length -
                                availableSubResources.length,
                        };

                        return result;
                    }
                } else if (isResourceTableGroup(resource)) {
                    //The table group should be handled in the resource group as its a child of the resource group
                    //This should never happen
                    throw new Error("ResourceTableGroup should not be here");
                } else {
                    const adjustedTimeRange = addMinutesTimeToTimeRange({
                        timeRange: resourceTimeRange,
                        durationBefore:
                            settings.preparationTime ??
                            (0 as DurationInMinutes),
                        durationAfter: settings.postTime,
                    });

                    const result: ResourceAvailability = {
                        resourceId,
                        available: isResourceAvailable({
                            timeRange: adjustedTimeRange,
                            requestedQuantity,
                            resource,
                            settings: settings,
                            reservations: resourceReservationInfo,
                            overrideSession: options?.overrideSession ?? false,
                        }),
                        availableSubResources: [],
                        subResourceCount_available: 0,
                        subResourceCount_unavailable: 0,
                    };
                    return result;
                }
            });
        })
        .filter(Boolean);
};
/*
 * Returns the best match resources for the requested quantity
 * @param requested The requested quantity
 * @param availableResources The available resources
 * @returns The best match resources
 * */
const filterBestMatchResources = (
    requested: number,
    availableResources: ResourceAvailabilityTimeRange[],
) => {
    const sorted = availableResources.sort(sortBy(r => r.availableQuantity));
    //const group = groupBy(availableResources, ar => ar.availableQuantity);

    for (const item of sorted) {
        const currentQuantity = item.availableQuantity;
        if (currentQuantity === "INFINITE" || currentQuantity >= requested) {
            return sorted.filter(r => r.availableQuantity === currentQuantity);
        }
    }

    return [];
};

const getSessionMinTimeRange = (session: BookableSession) => {
    return timeRangeByDurationFactory(
        session.start,
        //  addMs(
        convertTimeByGranularity(
            session.durationMinMax.min,
            session.durationGranularity,
            "MILLISECOND",
        ),

        // convertTimeByGranularity(
        //     session.bookableSettings.postTime,
        //     "MINUTE",
        //     "MILLISECOND",
        // ),
        //  ),
    );
};

//If any ResourceReferences contains a local resource (local to session) this returns true
const sessionOverridesResourceIds = (acquire: Bookable) => {
    const skuReferences = getBookableResourceReferences(acquire);
    return skuReferences.some(item => item.resource._type === "RESOURCE_LOCAL");
};

export {
    calculateAvailabilitySpecialized,
    findAllActiveResources,
    findAllActiveResourcesByBookable,
    findAllLocalMainResources,
    getSessionMinTimeRange,
    sessionOverridesResourceIds
};
export type { ResourceAvailability, TimeRangeResourceAvailability };

